import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "react-toastify";
import {
  UserPlus, Shield, ShieldAlert, ShieldCheck, Trash2,
  LogOut, ChevronDown, ChevronUp, Check, X, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  view_dashboard: "View Dashboard",
  view_orders: "View Orders",
  manage_orders: "Manage Orders",
  view_leads: "View Leads & Clients",
  manage_leads: "Manage Leads & Clients",
  view_designs: "View Designs",
  manage_designs: "Reply to Designs",
  view_analytics: "View Analytics",
  manage_campaigns: "Send Campaigns",
  manage_blog: "Manage Blog",
  view_reviews: "View Reviews",
  manage_staff: "Manage Staff",
};

const ALL_PERMISSIONS = Object.keys(PERMISSION_LABELS);

const ROLE_BADGE = {
  super_admin: { label: "Super Admin", cls: "bg-red-100 text-red-800 border-red-200", icon: ShieldAlert },
  admin: { label: "Admin", cls: "bg-orange-100 text-orange-800 border-orange-200", icon: ShieldCheck },
  sales: { label: "Sales", cls: "bg-blue-100 text-blue-800 border-blue-200", icon: Shield },
};

export default function StaffManagement() {
  const { user: currentUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "sales" });
  const [inviting, setInviting] = useState(false);

  const isSuperAdmin = currentUser?.role === "super_admin";

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/staff`, { withCredentials: true });
      setStaff(data);
    } catch {
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const addStaff = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await axios.post(`${API}/auth/invite-staff`, newUser, { withCredentials: true });
      toast.success(res.data?.message || "Invite sent!");
      setShowAdd(false);
      setNewUser({ name: "", email: "", role: "sales" });
      fetchStaff();
    } catch (err) {
      console.error("Invite error:", err);
      const msg = err.response?.data?.detail || err.message || "Failed to invite staff";
      toast.error(msg);
    } finally {
      setInviting(false);
    }
  };

  const updatePermissions = async (userId, permissions) => {
    try {
      await axios.put(`${API}/auth/staff/${userId}/permissions`, { permissions }, { withCredentials: true });
      toast.success("Permissions updated");
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update permissions");
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/auth/staff/${userId}/role`, { role }, { withCredentials: true });
      toast.success(`Role updated to ${role}`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const toggleActive = async (userId) => {
    try {
      const { data } = await axios.put(`${API}/auth/staff/${userId}/toggle-active`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle status");
    }
  };

  const revokeSessions = async (userId, email) => {
    if (!window.confirm(`Revoke all sessions for ${email}? They'll be logged out immediately.`)) return;
    try {
      const { data } = await axios.post(`${API}/auth/staff/${userId}/revoke-sessions`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke sessions");
    }
  };

  const deleteStaff = async (userId, email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/auth/staff/${userId}`, { withCredentials: true });
      toast.success("Staff member deleted");
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const togglePermission = (userId, currentPerms, perm) => {
    const updated = currentPerms.includes(perm)
      ? currentPerms.filter(p => p !== perm)
      : [...currentPerms, perm];
    updatePermissions(userId, updated);
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Loading staff...</div>;

  return (
    <div data-testid="staff-management" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Staff Management</h2>
          <p className="text-sm text-gray-500">{staff.length} team member{staff.length !== 1 && "s"}</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => setShowAdd(!showAdd)} className="bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="add-staff-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Add Staff
          </Button>
        )}
      </div>

      {/* Add Staff Form */}
      {showAdd && isSuperAdmin && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <form onSubmit={addStaff} className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} required placeholder="Full Name" data-testid="new-staff-name" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required placeholder="email@fitprintbros.com" data-testid="new-staff-email" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}
                  className="w-full h-10 px-3 border rounded-md text-sm" data-testid="new-staff-role">
                  <option value="sales">Sales</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="sm:col-span-3 flex gap-2">
                <Button type="submit" disabled={inviting} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-new-staff">
                  <Mail className="w-4 h-4 mr-1" /> {inviting ? "Sending..." : "Send Invite"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              </div>
              <p className="sm:col-span-3 text-xs text-gray-500">They'll receive an email to set their own password.</p>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Staff List */}
      <div className="space-y-2">
        {staff.map(member => {
          const badge = ROLE_BADGE[member.role] || ROLE_BADGE.sales;
          const BadgeIcon = badge.icon;
          const isExpanded = expandedUser === member.id;
          const isSelf = member.id === currentUser?.id;
          const isTargetSuperAdmin = member.role === "super_admin";
          const perms = member.permissions || [];

          return (
            <Card key={member.id} className={`transition ${!member.is_active ? "opacity-50 border-red-200" : ""}`} data-testid={`staff-card-${member.id}`}>
              <CardContent className="p-3">
                {/* Header row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${member.is_active ? "bg-gray-100" : "bg-red-100"}`}>
                      <BadgeIcon className={`w-4 h-4 ${member.is_active ? "text-gray-600" : "text-red-500"}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-900">{member.name || member.email}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${badge.cls}`}>{badge.label}</span>
                        {!member.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 font-bold">DEACTIVATED</span>}
                        {isSelf && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-bold">YOU</span>}
                      </div>
                      <p className="text-xs text-gray-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {isSuperAdmin && !isTargetSuperAdmin && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(member.id)} title={member.is_active ? "Deactivate" : "Activate"}
                          className={`h-8 px-2 ${member.is_active ? "text-gray-500 hover:text-red-600" : "text-green-600 hover:text-green-700"}`} data-testid={`toggle-active-${member.id}`}>
                          {member.is_active ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => revokeSessions(member.id, member.email)} title="Revoke Sessions"
                          className="h-8 px-2 text-gray-500 hover:text-orange-600" data-testid={`revoke-sessions-${member.id}`}>
                          <LogOut className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteStaff(member.id, member.email)} title="Delete"
                          className="h-8 px-2 text-gray-500 hover:text-red-600" data-testid={`delete-staff-${member.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    {isSuperAdmin && !isTargetSuperAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => setExpandedUser(isExpanded ? null : member.id)}
                        className="h-8 px-2 text-gray-400" data-testid={`expand-staff-${member.id}`}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expanded: Permissions & Role */}
                {isExpanded && isSuperAdmin && !isTargetSuperAdmin && (
                  <div className="mt-3 pt-3 border-t space-y-3">
                    {/* Role Selector */}
                    <div className="flex items-center gap-3">
                      <Label className="text-xs font-semibold text-gray-700 w-12">Role:</Label>
                      <div className="flex gap-2">
                        {["admin", "sales"].map(r => (
                          <button key={r} onClick={() => updateRole(member.id, r)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${member.role === r ? "bg-orange-500 text-white border-orange-500" : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"}`}
                            data-testid={`role-${r}-${member.id}`}>
                            {r === "admin" ? "Admin" : "Sales"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Permissions Grid */}
                    <div>
                      <Label className="text-xs font-semibold text-gray-700 mb-2 block">Permissions:</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {ALL_PERMISSIONS.map(perm => (
                          <label key={perm} className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs cursor-pointer transition ${perms.includes(perm) ? "bg-green-50 border-green-300 text-green-800" : "bg-gray-50 border-gray-200 text-gray-500"}`}
                            data-testid={`perm-${perm}-${member.id}`}>
                            <input type="checkbox" checked={perms.includes(perm)} onChange={() => togglePermission(member.id, perms, perm)}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                            <span className="leading-tight">{PERMISSION_LABELS[perm]}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {staff.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Shield className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>No staff members yet.</p>
        </div>
      )}
    </div>
  );
}
